import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../common';
import { Button } from '../../common';
import { useChatStore } from '@auw/stores';
import { useFeatureFlags } from '@auw/feature-flags';
import {
  billingStatusLabel,
  formatDateOnlyOrDash,
} from '../utils';
import { useBillingPortal } from '../hooks/useBillingPortal';
import { useBillingStatus } from '../hooks/useBillingStatus';
import {
  ActionRow,
  BillingMetaGrid,
  BillingMetaItem,
  BillingMetaLabel,
  BillingMetaValue,
  Divider,
  InlineError,
  InlineInfo,
  PlanBox,
  PlanName,
  PlanNameRow,
  PlanPrice,
  SectionBlock,
  SectionHeader,
  SectionTitle,
} from '../styles';

export const BillingTab: React.FC = () => {
  const { t } = useTranslation('settings');
  const addToast = useChatStore((s) => s.addToast);
  /* P11-5d: gate the Paddle customer portal CTA. SaaS tenants keep the
   * "manage payment / view invoice" buttons; on-prem tenants ship a
   * read-only billing snapshot with no portal redirect. */
  const customerPortalEnabled = useFeatureFlags().isEnabled('billing.customerPortal');
  const { billing, loading, error, refresh } = useBillingStatus({
    fallbackErrorMessage: t('billing.loadFail'),
  });
  const { portalLoading, openPortal } = useBillingPortal({
    subscriptionId: billing?.subscription_id,
    addToast,
    errorTitle: t('billing.portalFail'),
    errorFallbackMessage: t('billing.portalFailDesc'),
  });

  const paymentMethod = billing?.payment_method;
  const paymentMethodLabel = paymentMethod?.type === 'card'
    ? `${paymentMethod.brand || 'CARD'} •••• ${paymentMethod.last4 || '----'}`
    : paymentMethod?.type
      ? `${paymentMethod.type.toUpperCase()}`
      : t('billing.noPaymentMethod');

  return (
    <>
      <SectionBlock>
        <SectionHeader>
          <SectionTitle>{t('billing.paymentMethod')}</SectionTitle>
        </SectionHeader>
        <Divider />

        {loading && <InlineInfo>{t('billing.loading')}</InlineInfo>}
        {error && <InlineError>{error}</InlineError>}

        <PlanBox>
          <PlanNameRow>
            <PlanName>{t('billing.defaultPayment')}</PlanName>
            <Badge variant={paymentMethod ? 'success' : 'default'}>
              {paymentMethod ? t('billing.registered') : t('billing.notRegistered')}
            </Badge>
          </PlanNameRow>
          <PlanPrice>{paymentMethodLabel}</PlanPrice>
          <BillingMetaGrid>
            <BillingMetaItem>
              <BillingMetaLabel>{t('billing.billingCycle')}</BillingMetaLabel>
              <BillingMetaValue>{billing?.billing_cycle || '-'}</BillingMetaValue>
            </BillingMetaItem>
            <BillingMetaItem>
              <BillingMetaLabel>{t('billing.nextBillingDate')}</BillingMetaLabel>
              <BillingMetaValue>{formatDateOnlyOrDash(billing?.next_billed_at)}</BillingMetaValue>
            </BillingMetaItem>
            <BillingMetaItem>
              <BillingMetaLabel>{t('billing.subscriptionStatus')}</BillingMetaLabel>
              <BillingMetaValue>{billingStatusLabel(billing?.billing_status)}</BillingMetaValue>
            </BillingMetaItem>
          </BillingMetaGrid>
        </PlanBox>

        <ActionRow>
          {customerPortalEnabled && (
            <>
              <Button
                variant="ghost"
                size="sm"
                isLoading={portalLoading}
                onClick={() => { void openPortal('billing'); }}
              >
                {t('billing.managePayment')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                isLoading={portalLoading}
                onClick={() => { void openPortal('manage'); }}
              >
                {t('billing.viewInvoice')}
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            isLoading={loading}
            onClick={() => { void refresh(); }}
          >
            {t('billing.refreshStatus')}
          </Button>
        </ActionRow>
      </SectionBlock>
    </>
  );
};
